import React, { ChangeEvent, Dispatch, MouseEvent, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import { fc } from 'i18n';
import { List } from './list';
import { chevronDown } from 'assets/icons';
import { ISelectOption } from 'types/select';
import Input from 'components/molecules/input';
import { getIsSubstring } from 'helpers/getIsSubString';
import { useOnClickOutside } from 'hooks/useOnClickOutside';

import styles from './styles.module.css';

interface ISelectProps<T> {
  disabled?: boolean;
  withSearch?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: string;
  selected?: ISelectOption<T> | null;
  options: ISelectOption<T>[] | null;
  onChange?: Dispatch<ISelectOption<T>>;
}

export const SelectPicker = <T,>(props: ISelectProps<T>) => {
  const { withSearch, selected, options, onChange, readOnly, ...rest } = props;

  const wrapperRef = useRef(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');

  const onClose = () => {
    setIsOpen(false);
    setFilter('');
  };

  useOnClickOutside([wrapperRef], onClose);

  const filteredOptions = useMemo(
    () => (!filter ? options : options?.filter(({ label }) => getIsSubstring(label, filter))),
    [options, filter],
  );

  const placeholder = withSearch ? selected?.label || fc('select') : fc('select');

  const contentClassName = classnames(styles.content, { [styles.notEmpty]: !!selected && withSearch });

  const onFilterChange = (event: ChangeEvent<HTMLInputElement>) => setFilter(event.target.value);

  const toggleDropdown = () => {
    !readOnly && setIsOpen(!isOpen);
  };

  const selectOption = (option: ISelectOption<T>) => (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    !!onChange && onChange(option);
    onClose();
  };

  return (
    <div className={styles.wrapper}>
      <div className={contentClassName} ref={wrapperRef}>
        <Input
          placeholder={placeholder}
          label={fc('tracker name')}
          value={withSearch ? filter : selected?.label || ''}
          icon={chevronDown}
          onChange={onFilterChange}
          onClick={toggleDropdown}
          {...rest}
        />
        {isOpen && <List selectedId={selected?.value} options={filteredOptions || null} selectOption={selectOption} />}
      </div>
    </div>
  );
};
